import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Layers, Save, Trash2, Shuffle, Play as PlayIcon, Eraser, Stamp, X, RotateCw, FlipHorizontal } from "lucide-react";
import Navbar from "../components/Navbar";
import GameCanvas from "../game/GameCanvas";
import api from "../lib/api";
import { listShapes } from "../lib/levels";

// 8 rows x 12 cols — matches the engine's ROWS/COLS contract.
const ROWS = 8;
const COLS = 12;

// HP tiers 0..4. 0 = empty, 1 = cyan, 2 = gold, 3 = pink, 4 = purple-boss.
const TIER_PALETTE = [
  { bg: "rgba(255,255,255,0.03)", border: "rgba(255,255,255,0.1)", label: "Empty",  glow: "none" },
  { bg: "#00ffff",                border: "#00ffff",                label: "Tier 1 · 1 HP", glow: "0 0 6px rgba(0,255,255,0.7)" },
  { bg: "#ffd700",                border: "#ffd700",                label: "Tier 2 · 2 HP", glow: "0 0 6px rgba(255,215,0,0.7)" },
  { bg: "#ff007f",                border: "#ff007f",                label: "Tier 3 · 3 HP", glow: "0 0 8px rgba(255,0,127,0.7)" },
  { bg: "#b026ff",                border: "#b026ff",                label: "Boss · 6 HP",   glow: "0 0 12px rgba(176,38,255,0.8)" },
];

function emptyGrid() {
  return Array.from({ length: ROWS }, () => Array(COLS).fill(0));
}

function countBlocks(grid) {
  let n = 0;
  for (const row of grid) for (const v of row) if (v > 0) n++;
  return n;
}

// Normalize a shape's cells so the top-left of its bbox sits at (0, 0).
// Used by drag-to-place so stamps anchor at the cell the pilot drops on.
function normalizeCells(cells) {
  if (!cells || !cells.length) return [];
  let minR = Infinity, minC = Infinity;
  for (const [r, c] of cells) {
    if (r < minR) minR = r;
    if (c < minC) minC = c;
  }
  return cells.map(([r, c]) => [r - minR, c - minC]);
}

// Rotate cells 90° clockwise: (r, c) → (c, maxR - r). Output is renormalised
// back to (0, 0) bbox origin so it still drops where the pilot clicks.
function rotateCellsCw(cells) {
  if (!cells || !cells.length) return [];
  let maxR = 0;
  for (const [r] of cells) if (r > maxR) maxR = r;
  return normalizeCells(cells.map(([r, c]) => [c, maxR - r]));
}

// Mirror horizontally: (r, c) → (r, maxC - c). Renormalised.
function mirrorCellsH(cells) {
  if (!cells || !cells.length) return [];
  let maxC = 0;
  for (const [, c] of cells) if (c > maxC) maxC = c;
  return normalizeCells(cells.map(([r, c]) => [r, maxC - c]));
}

// Apply N 90° CW rotations + optional mirror to a base cell list.
function transformCells(base, rotation, mirrored) {
  let out = base;
  for (let i = 0; i < ((rotation % 4) + 4) % 4; i++) out = rotateCellsCw(out);
  if (mirrored) out = mirrorCellsH(out);
  return out;
}

// Tiny 12-column preview of a shape, coloured to match the active brush tier.
function ShapeThumb({ cells, tier }) {
  const color =
    tier === 1 ? "#00ffff" :
    tier === 2 ? "#ffd700" :
    tier === 3 ? "#ff007f" :
    tier === 4 ? "#b026ff" : "#00ffff";
  const grid = Array.from({ length: ROWS }, () => Array(COLS).fill(false));
  for (const [r, c] of cells) {
    if (r >= 0 && r < ROWS && c >= 0 && c < COLS) grid[r][c] = true;
  }
  return (
    <div
      className="grid gap-[1px] w-full"
      style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)`, aspectRatio: `${COLS} / ${ROWS}` }}
    >
      {grid.flat().map((on, i) => (
        <span
          key={i}
          style={{
            background: on ? color : "rgba(255,255,255,0.04)",
            boxShadow: on ? `0 0 3px ${color}aa` : "none",
          }}
        />
      ))}
    </div>
  );
}

export default function Builder() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [grid, setGrid] = useState(emptyGrid);
  const [brush, setBrush] = useState(1); // selected tier 0..4
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [speed, setSpeed] = useState(5.5);
  const [playing, setPlaying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null); // {tone, text}
  const [mine, setMine] = useState([]);
  const [remixSource, setRemixSource] = useState(null); // {code, author_name}
  // Drag-to-place stamp state — when armed, the next grid click drops the
  // shape with its bbox top-left anchored at the clicked cell. `hoverCell`
  // drives the translucent preview overlay so the pilot can see exactly
  // where the stamp will land before committing.
  const [armedStamp, setArmedStamp] = useState(null); // {id,label,normalized:[[r,c]]}
  const [hoverCell, setHoverCell] = useState(null); // {r, c}
  // Live transform on the armed stamp — rotation ∈ {0,1,2,3} quarter-turns
  // CW, mirrored H flips afterwards. Keyboard R / F, or the toolbar icons.
  const [stampRotation, setStampRotation] = useState(0);
  const [stampMirrored, setStampMirrored] = useState(false);

  // Derived: the final cell pattern that drops, after applying rotation +
  // mirror to the armed stamp's normalized base. Memoised so hover-preview
  // doesn't recompute on every mouse move.
  const armedTransformed = useMemo(() => {
    if (!armedStamp) return null;
    return transformCells(armedStamp.normalized, stampRotation, stampMirrored);
  }, [armedStamp, stampRotation, stampMirrored]);

  const total = useMemo(() => countBlocks(grid), [grid]);

  // If ?remix=CODE is present, fetch that community level and seed the editor
  // with its grid/name/desc/speed so the pilot can iterate on top of it.
  useEffect(() => {
    const code = params.get("remix");
    if (!code) return;
    (async () => {
      try {
        const r = await api.get(`/community/level/${code}`);
        setGrid(r.data.grid.map((row) => row.slice()));
        setName(`${r.data.name} (remix)`.slice(0, 48));
        setDesc(r.data.description || "");
        setSpeed(r.data.ball_speed || 5.5);
        setRemixSource({ code, author_name: r.data.author_name });
        setMsg({ tone: "ok", text: `Remixing @${r.data.author_name}'s "${r.data.name}"` });
        // Strip the query param so a reload doesn't re-apply the remix
        const next = new URLSearchParams(params);
        next.delete("remix");
        setParams(next, { replace: true });
      } catch (_) {
        setMsg({ tone: "warn", text: `Could not load remix source ${code}` });
      }
    })();
  }, [params, setParams]);

  const refreshMine = useCallback(async () => {
    try {
      const r = await api.get("/community/my-levels");
      setMine(r.data.items || []);
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => { refreshMine(); }, [refreshMine]);

  const setCell = (r, c) => {
    setGrid((g) => {
      if (g[r][c] === brush) return g; // no-op
      const next = g.map((row) => row.slice());
      next[r][c] = brush;
      return next;
    });
  };

  // Drag-paint: mousedown on a cell fills, mouseenter while dragging fills too.
  const [dragging, setDragging] = useState(false);
  useEffect(() => {
    const up = () => setDragging(false);
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, []);

  const previewLevel = useMemo(
    () => ({ ballSpeed: speed, grid, name: name || "Preview", seed: `preview-${total}` }),
    [grid, speed, name, total]
  );

  const randomize = () => {
    const next = emptyGrid();
    const rows = 3 + Math.floor(Math.random() * 4);
    for (let r = 1; r <= rows; r++) {
      for (let c = 1; c < COLS - 1; c++) {
        const roll = Math.random();
        if (roll < 0.3) continue;
        if (roll < 0.32) next[r][c] = 4;
        else if (roll < 0.55) next[r][c] = 3;
        else if (roll < 0.78) next[r][c] = 2;
        else next[r][c] = 1;
      }
    }
    setGrid(next);
  };

  // Paint a pre-made shape (rocket, saucer, etc.) at the current brush tier
  // anchored at `(rowOrigin, colOrigin)` — the bbox top-left of the given
  // pre-transformed cells. Stamps compose over existing blocks so pilots
  // can mix-and-match.
  const stampShapeAt = (cellsFinal, rowOrigin, colOrigin) => {
    const tier = brush > 0 ? brush : 1;
    setGrid((g) => {
      const next = g.map((row) => row.slice());
      for (const [dr, dc] of cellsFinal) {
        const rr = rowOrigin + dr;
        const cc = colOrigin + dc;
        if (rr >= 0 && rr < ROWS && cc >= 0 && cc < COLS) {
          next[rr][cc] = tier;
        }
      }
      return next;
    });
  };

  // Click a stamp button to "arm" it — clicking again disarms. Resets any
  // in-flight rotation / mirror so each arm starts in the canonical pose.
  const armStamp = (shape) => {
    setArmedStamp((cur) => {
      if (cur && cur.id === shape.id) return null;
      return { id: shape.id, label: shape.label, normalized: normalizeCells(shape.cells) };
    });
    setStampRotation(0);
    setStampMirrored(false);
  };

  // Keyboard shortcuts while a stamp is armed:
  //   R  → rotate 90° CW
  //   F  → flip horizontally
  //   Esc → disarm
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setArmedStamp(null);
      // Only intercept R / F when a stamp is armed AND the pilot isn't
      // typing into an input — otherwise we'd steal characters from the
      // name/description fields.
      if (!armedStamp) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (e.key === "r" || e.key === "R") {
        e.preventDefault();
        setStampRotation((v) => (v + 1) % 4);
      } else if (e.key === "f" || e.key === "F") {
        e.preventDefault();
        setStampMirrored((m) => !m);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [armedStamp]);

  const save = async () => {
    setMsg(null);
    if (name.trim().length < 3) {
      setMsg({ tone: "warn", text: "Name must be at least 3 characters." });
      return;
    }
    if (total < 6) {
      setMsg({ tone: "warn", text: "Place at least 6 blocks before saving." });
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/community/levels", {
        name: name.trim(),
        description: desc.trim(),
        grid,
        ball_speed: speed,
        remix_of: remixSource?.code || null,
      });
      setMsg({ tone: "ok", text: `Saved! Share code: ${r.data.code}` });
      setRemixSource(null);
      await refreshMine();
    } catch (e) {
      setMsg({ tone: "warn", text: e.response?.data?.detail || "Save failed." });
    } finally {
      setSaving(false);
    }
  };

  const deleteMine = async (code) => {
    setMsg(null);
    try {
      await api.delete(`/community/level/${code}`);
      await refreshMine();
      setMsg({ tone: "ok", text: `Deleted ${code}.` });
    } catch (e) {
      setMsg({ tone: "warn", text: e.response?.data?.detail || "Delete failed." });
    }
  };

  if (playing) {
    return (
      <div className="relative min-h-screen">
        <div className="starfield" />
        <Navbar />
        <main className="relative z-10 max-w-5xl mx-auto px-4 pt-24 pb-16 fade-in">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <div className="font-mono-cosmic text-[11px] tracking-[0.35em] text-[#00ffff] uppercase">
                // BUILDER · PLAYTEST
              </div>
              <div className="font-heading font-black uppercase text-2xl tracking-tighter text-white">
                {name || "Untitled"}
              </div>
            </div>
            <button
              onClick={() => setPlaying(false)}
              className="btn-neon"
              data-testid="builder-playtest-exit"
            >
              Back to editor
            </button>
          </div>
          <GameCanvas
            key={`playtest-${total}-${speed}`}
            level={1}
            levelOverride={previewLevel}
            onComplete={() => setPlaying(false)}
            onExit={() => setPlaying(false)}
            bestScore={0}
          />
        </main>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen">
      <div className="starfield" />
      <Navbar />
      <main className="relative z-10 max-w-7xl mx-auto px-4 pt-24 pb-16 fade-in">
        <header className="mb-6">
          <div className="flex items-center gap-2 font-mono-cosmic text-[11px] tracking-[0.35em] text-[#b026ff] uppercase mb-2">
            <Layers className="w-3.5 h-3.5" />
            Level Builder · 8 × 12 grid
          </div>
          <h1 className="font-heading font-black uppercase text-3xl sm:text-4xl tracking-tighter">
            Build a Cosmic Level
          </h1>
          <p className="text-white/60 text-sm mt-1">
            Paint blocks, pick a ball speed, save to share. Click or drag across cells.
            Your levels appear on the Community board when saved.
          </p>
        </header>

        {msg && (
          <div
            className={`mb-4 glass p-3 border font-mono-cosmic text-xs tracking-widest uppercase ${
              msg.tone === "ok"
                ? "border-[#00ff41]/60 text-[#00ff41]"
                : "border-[#ff007f]/60 text-[#ff007f]"
            }`}
            data-testid="builder-status"
          >
            {msg.text}
          </div>
        )}

        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-5 items-start">
          <div className="glass p-4 border border-white/15 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-3" data-testid="builder-palette">
              {TIER_PALETTE.map((t, i) => (
                <button
                  key={i}
                  onClick={() => setBrush(i)}
                  className={`px-3 py-1.5 border font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-2 ${
                    brush === i ? "border-white text-white" : "border-white/15 text-white/60 hover:text-white"
                  }`}
                  data-testid={`builder-brush-${i}`}
                  title={t.label}
                >
                  <span
                    className="w-4 h-4 border"
                    style={{
                      background: i === 0 ? "transparent" : t.bg,
                      borderColor: t.border,
                      boxShadow: i === 0 ? "none" : t.glow,
                    }}
                  />
                  {i === 0 ? "Erase" : `T${i}`}
                </button>
              ))}
              <button
                onClick={() => setGrid(emptyGrid())}
                className="ml-auto px-3 py-1.5 border border-white/15 hover:border-[#ff007f]/60 hover:text-[#ff007f] text-white/60 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1.5"
                data-testid="builder-clear"
              >
                <Eraser className="w-3 h-3" /> Clear
              </button>
              <button
                onClick={randomize}
                className="px-3 py-1.5 border border-[#b026ff]/40 text-[#b026ff] hover:bg-[#b026ff]/10 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase transition inline-flex items-center gap-1.5"
                data-testid="builder-randomize"
              >
                <Shuffle className="w-3 h-3" /> Random
              </button>
            </div>

            <div
              className="grid gap-0.5 select-none relative"
              style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)` }}
              onMouseLeave={() => { setDragging(false); setHoverCell(null); }}
              data-testid="builder-grid"
            >
              {grid.map((row, r) =>
                row.map((v, c) => {
                  const t = TIER_PALETTE[v] || TIER_PALETTE[0];
                  // When a stamp is armed, show a translucent preview ring on
                  // every cell that would receive paint at the current hover.
                  let previewBg = null;
                  if (armedStamp && armedTransformed && hoverCell) {
                    for (const [dr, dc] of armedTransformed) {
                      if (hoverCell.r + dr === r && hoverCell.c + dc === c) {
                        const previewColor =
                          brush === 0 || brush === 1 ? "#00ffff" :
                          brush === 2 ? "#ffd700" :
                          brush === 3 ? "#ff007f" : "#b026ff";
                        previewBg = previewColor;
                        break;
                      }
                    }
                  }
                  const onCellDown = (e) => {
                    e.preventDefault();
                    if (armedStamp && armedTransformed) {
                      stampShapeAt(armedTransformed, r, c);
                      // Keep the stamp armed so pilots can drop the same shape
                      // multiple times — Esc or click-again disarms.
                      return;
                    }
                    setDragging(true);
                    setCell(r, c);
                  };
                  return (
                    <button
                      key={`${r}-${c}`}
                      type="button"
                      className="aspect-square border transition relative"
                      style={{
                        background: previewBg ? `${previewBg}40` : t.bg,
                        borderColor: previewBg ? previewBg : t.border,
                        boxShadow: previewBg
                          ? `0 0 6px ${previewBg}aa`
                          : (v === 0 ? "none" : t.glow),
                      }}
                      onMouseDown={onCellDown}
                      onMouseEnter={() => {
                        if (armedStamp) setHoverCell({ r, c });
                        else if (dragging) setCell(r, c);
                      }}
                      onTouchStart={onCellDown}
                      data-testid={`builder-cell-${r}-${c}`}
                      aria-label={`cell ${r} ${c}`}
                    />
                  );
                })
              )}
            </div>

            <div className="mt-3 flex items-center justify-between gap-2 font-mono-cosmic text-[10px] tracking-[0.3em] uppercase text-white/50">
              <span data-testid="builder-blockcount">Blocks · {total}</span>
              {armedStamp ? (
                <div className="flex items-center gap-1.5" data-testid="builder-armed-hint">
                  <button
                    type="button"
                    onClick={() => setStampRotation((v) => (v + 1) % 4)}
                    className="inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#b026ff]/60 text-[#b026ff] text-[9px] tracking-[0.2em] uppercase hover:bg-[#b026ff]/10"
                    title="Rotate 90° CW (R)"
                    data-testid="builder-stamp-rotate"
                  >
                    <RotateCw className="w-2.5 h-2.5" /> {stampRotation * 90}°
                  </button>
                  <button
                    type="button"
                    onClick={() => setStampMirrored((m) => !m)}
                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 border text-[9px] tracking-[0.2em] uppercase transition ${
                      stampMirrored
                        ? "border-[#00ffff] text-[#00ffff] bg-[#00ffff]/10"
                        : "border-[#00ffff]/40 text-[#00ffff] hover:bg-[#00ffff]/10"
                    }`}
                    title="Flip horizontal (F)"
                    data-testid="builder-stamp-mirror"
                  >
                    <FlipHorizontal className="w-2.5 h-2.5" /> {stampMirrored ? "MIRRORED" : "FLIP"}
                  </button>
                  <span className="text-[#b026ff] ml-1 hidden sm:inline">
                    {armedStamp.label} · R / F
                  </span>
                </div>
              ) : (
                <span className="text-white/30">Tap or drag to paint</span>
              )}
            </div>
          </div>

          <aside className="glass p-4 border border-white/15 space-y-4" data-testid="builder-sidebar">
            <div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-1">
                Name
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={48}
                placeholder="Supernova Tunnel"
                className="w-full bg-black/40 border border-white/15 focus:border-[#b026ff] px-3 py-2 text-white font-mono-cosmic text-sm outline-none transition"
                data-testid="builder-name"
              />
            </div>
            <div>
              <div className="font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-1">
                Description
              </div>
              <textarea
                value={desc}
                onChange={(e) => setDesc(e.target.value)}
                maxLength={160}
                rows={3}
                placeholder="Short hint or flavour text..."
                className="w-full bg-black/40 border border-white/15 focus:border-[#b026ff] px-3 py-2 text-white font-mono-cosmic text-xs outline-none transition resize-none"
                data-testid="builder-desc"
              />
              <div className="text-right font-mono-cosmic text-[9px] tracking-[0.25em] text-white/30 mt-0.5">
                {desc.length}/160
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between font-mono-cosmic text-[9px] tracking-[0.3em] uppercase text-white/50 mb-1">
                <span>Ball Speed</span>
                <span className="text-[#00ffff]">{speed.toFixed(1)}</span>
              </div>
              <input
                type="range"
                min="3"
                max="9.5"
                step="0.1"
                value={speed}
                onChange={(e) => setSpeed(parseFloat(e.target.value))}
                className="w-full accent-[#b026ff]"
                data-testid="builder-speed"
              />
              <div className="flex justify-between font-mono-cosmic text-[8px] tracking-[0.25em] text-white/30 mt-1">
                <span>Chill</span><span>Balanced</span><span>Brutal</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setPlaying(true)}
                disabled={total < 6}
                className="py-2 border border-[#00ffff] text-[#00ffff] hover:bg-[#00ffff]/10 disabled:border-white/10 disabled:text-white/30 disabled:cursor-not-allowed font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition inline-flex items-center justify-center gap-1.5"
                data-testid="builder-playtest"
              >
                <PlayIcon className="w-3 h-3" /> Playtest
              </button>
              <button
                onClick={save}
                disabled={saving || total < 6}
                className="py-2 border border-[#b026ff] text-[#b026ff] hover:bg-[#b026ff]/10 disabled:border-white/10 disabled:text-white/30 disabled:cursor-not-allowed font-mono-cosmic text-[10px] tracking-[0.3em] uppercase transition inline-flex items-center justify-center gap-1.5"
                data-testid="builder-save"
              >
                <Save className="w-3 h-3" /> Save & Share
              </button>
            </div>

            <div className="pt-3 border-t border-white/10">
              <div className="flex items-center gap-1.5 font-mono-cosmic text-[10px] tracking-[0.3em] text-[#b026ff] uppercase mb-2">
                <Stamp className="w-3 h-3" /> Shape Stamps
                {armedStamp && (
                  <button
                    onClick={() => setArmedStamp(null)}
                    className="ml-auto inline-flex items-center gap-1 px-1.5 py-0.5 border border-[#ff007f]/60 text-[#ff007f] text-[8px] tracking-[0.2em] uppercase hover:bg-[#ff007f]/10"
                    data-testid="builder-stamp-cancel"
                  >
                    <X className="w-2.5 h-2.5" /> Cancel
                  </button>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="builder-shape-stamps">
                {listShapes().map((shape) => {
                  const isArmed = armedStamp?.id === shape.id;
                  // Boss stamps use the crimson "Boss · X" label and get a
                  // pink accent so they read as special / heavy compared to
                  // the standard cyan space-iconography stamps.
                  const isBossStamp = shape.id.startsWith("mini") || shape.label.startsWith("Boss");
                  const armedColor = isBossStamp ? "#ff007f" : "#b026ff";
                  return (
                    <button
                      key={shape.id}
                      type="button"
                      onClick={() => armStamp(shape)}
                      className={`group px-1 py-1 border transition flex flex-col items-center gap-0.5 ${
                        isArmed
                          ? "ring-1"
                          : isBossStamp
                            ? "border-[#ff007f]/40 hover:border-[#ff007f]"
                            : "border-white/10 hover:border-[#b026ff]"
                      }`}
                      style={
                        isArmed
                          ? {
                              borderColor: armedColor,
                              backgroundColor: `${armedColor}1a`,
                              boxShadow: `0 0 10px ${armedColor}55`,
                            }
                          : undefined
                      }
                      data-testid={`builder-stamp-${shape.id}`}
                      title={isArmed
                        ? "Click grid to place · click again to disarm"
                        : `Arm ${shape.label} stamp`}
                    >
                      <ShapeThumb cells={shape.cells} tier={brush || 1} />
                      <span
                        className={`font-mono-cosmic text-[8px] tracking-[0.1em] uppercase truncate w-full text-center ${
                          isArmed
                            ? ""
                            : isBossStamp
                              ? "text-[#ff007f]/90 group-hover:text-[#ff007f]"
                              : "text-white/60 group-hover:text-[#b026ff]"
                        }`}
                        style={isArmed ? { color: armedColor } : undefined}
                      >
                        {shape.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              <div className="font-mono-cosmic text-[8px] tracking-[0.25em] uppercase text-white/40 mt-1.5">
                Click a stamp to arm · then click any grid cell to drop it.
                <br />
                <span className="text-[#b026ff]/60">R rotates · F mirrors · Esc cancels</span>
              </div>
            </div>

            <div className="pt-3 border-t border-white/10">
              <div className="font-mono-cosmic text-[10px] tracking-[0.3em] text-white/50 uppercase mb-2">
                Your Levels · {mine.length}
              </div>
              {mine.length === 0 ? (
                <div className="text-white/40 font-mono-cosmic text-[10px] tracking-[0.25em] uppercase">
                  // SAVE YOUR FIRST LEVEL
                </div>
              ) : (
                <ul className="space-y-1.5 max-h-60 overflow-y-auto pr-1">
                  {mine.map((ld) => (
                    <li
                      key={ld.code}
                      className="flex items-center justify-between gap-2 border border-white/10 px-2 py-1.5"
                      data-testid={`builder-mine-${ld.code}`}
                    >
                      <div className="min-w-0">
                        <div className="text-white text-xs truncate">{ld.name}</div>
                        <div className="font-mono-cosmic text-[9px] tracking-[0.25em] uppercase text-white/40">
                          {ld.code} · {ld.plays}▶ · {ld.stars_count > 0 ? `★${ld.stars_avg.toFixed(1)}` : "unrated"}
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => navigate(`/community/${ld.code}`)}
                          className="w-7 h-7 border border-white/15 hover:border-[#00ffff] hover:text-[#00ffff] text-white/60 flex items-center justify-center"
                          title="Open"
                          data-testid={`builder-mine-open-${ld.code}`}
                        >
                          <PlayIcon className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => deleteMine(ld.code)}
                          className="w-7 h-7 border border-white/15 hover:border-[#ff007f] hover:text-[#ff007f] text-white/60 flex items-center justify-center"
                          title="Delete"
                          data-testid={`builder-mine-delete-${ld.code}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}
